"use client"

import { useState, useEffect, useLayoutEffect, useMemo, useRef, type ReactNode } from "react"
import Image from "next/image"
import { motion, AnimatePresence } from "framer-motion"
import { cn } from "@/shared/lib/utils"
import { ImageZoomLightbox } from "@/shared/components/image-zoom-lightbox"
import {
  GenerationImageLoaderBackdrop,
  SlotProgressRing,
} from "@/shared/components/generation-image-loader"
import { ExpandableClampedText } from "@/shared/components/expandable-clamped-text"
import { ImageFallback } from "@/shared/components/ui/image-fallback"
import { useImageFallback } from "@/shared/hooks/use-image-fallback"
import type { GenerationRow } from "@/shared/lib/supabase/types"
import { useMultiSlotProgress } from "../hooks/use-simulated-progress"
import type { GenerationProgressState } from "../hooks/mutations/use-generate-image"
import { isTosViolationMessage } from "../lib/tos-violation"
import { Loader2 } from "lucide-react"

interface GenerationFeedProps {
  generations: GenerationRow[]
  /** The generation currently being created (in-flight) */
  pendingGeneration?: {
    prompt: string
    style: string
    aspectRatio: string
    numImages: number
  } | null
  /** Streamed backend progress — used as a floor; simulated UI keeps advancing. */
  generationProgress?: GenerationProgressState | null
  isPending: boolean
}

const GENERATE_DEFAULT_IMAGE = "/images/paywall/generate-default.png"

// Host only — safe to send to analytics (no path/query, so no user-authored content).
function getUrlHost(url: string): string {
  try {
    return new URL(url).host
  } catch {
    return "unknown"
  }
}

function getOutputUrls(gen: GenerationRow): string[] {
  const params = gen.parameters as Record<string, unknown>
  return (
    (params?.output_urls as string[]) ??
    (gen.output_url ? [gen.output_url] : [])
  ).filter(Boolean)
}

// One thumbnail in the completed grid. Uses the shared image-fallback hook so a
// broken optimizer URL retries once, then renders the branded ImageFallback
// (instead of a blur layer stuck at opacity 0) and emits a captured $exception
// with safe metadata — matching gallery-card / image-detail / lightbox.
function FeedThumbnail({
  generation,
  url,
  index,
  outputCount,
  cssAspect,
  aspectRatio,
  skipImageEntrance,
  onZoom,
}: {
  generation: GenerationRow
  url: string
  index: number
  outputCount: number
  cssAspect: string
  aspectRatio: string
  skipImageEntrance?: boolean
  onZoom: (url: string) => void
}) {
  const { status, reloadKey, onLoad, onError } = useImageFallback({
    src: url,
    scope: "generation-feed-thumbnail",
    extra: {
      surface: "generation-feed",
      generation_id: generation.id,
      media_url_host: getUrlHost(url),
      aspect_ratio: aspectRatio,
    },
  })
  const loaded = status === "loaded"
  const failed = status === "error"

  return (
    <button
      type="button"
      onClick={() => onZoom(url)}
      className="relative overflow-hidden rounded-none border border-border/20 cursor-zoom-in hover:ring-2 hover:ring-white/10 transition-[box-shadow] focus:outline-none focus:ring-2 focus:ring-primary/50 bg-background"
      style={{ aspectRatio: cssAspect }}
      aria-label="View image full size"
    >
      {failed ? (
        <ImageFallback className="absolute inset-0" />
      ) : (
        <motion.div
          initial={false}
          animate={
            skipImageEntrance || loaded
              ? { opacity: 1, filter: "blur(0px)" }
              : { opacity: 0, filter: "blur(24px)" }
          }
          transition={
            skipImageEntrance
              ? { duration: 0 }
              : {
                  duration: 0.85,
                  delay: loaded ? index * 0.05 : 0,
                  ease: [0.22, 0.1, 0.22, 1],
                }
          }
          className="absolute inset-0"
        >
          <Image
            key={reloadKey}
            src={url}
            alt={generation.prompt}
            fill
            className="object-cover"
            sizes={
              outputCount === 1
                ? "(max-width: 768px) 100vw, 500px"
                : "(max-width: 768px) 50vw, 250px"
            }
            onLoad={onLoad}
            onError={onError}
            priority={outputCount <= 2 && index === 0}
          />
        </motion.div>
      )}
    </button>
  )
}

function FeedItem({
  generation,
  /** When set, replaces the default image grid (seamless loader → image handoff in-slot) */
  handoffImageSlot,
  /** After in-slot handoff ends, show final grid without opacity-0 flash (cache hit) */
  skipImageEntrance,
}: {
  generation: GenerationRow
  handoffImageSlot?: ReactNode
  skipImageEntrance?: boolean
}) {
  const [zoomUrl, setZoomUrl] = useState<string | null>(null)

  const params = generation.parameters as Record<string, unknown>
  const outputUrls = (params?.output_urls as string[]) ??
    (generation.output_url ? [generation.output_url] : [])
  const style = (params?.style as string) ?? generation.style
  const aspectRatio = (params?.aspectRatio as string) ?? "1:1"

  // Convert "W:H" to CSS aspect-ratio "W/H" (handles 2:3, 2.35:1, etc.)
  const cssAspect = aspectRatio.includes(":") ? aspectRatio.replace(":", "/") : "1/1"

  const isVertical = ["9:16", "3:4", "2:3", "4:5"].includes(aspectRatio)

  return (
    <div className="flex flex-col gap-3 py-6">
      {/* Prompt + Model badge */}
      <div className="flex items-start gap-3 max-w-2xl">
        <div className="rounded-lg bg-card/60 border border-border/20 px-3 py-2 max-w-sm">
          <ExpandableClampedText
            text={generation.prompt}
            lines={2}
            className="text-sm text-foreground"
            moreLabel="Read more"
            lessLabel="Show less"
          />
        </div>
        {style && (
          <span className="mt-1 rounded-full bg-card/40 border border-border/20 px-2 py-0.5 text-[10px] font-medium text-muted-foreground capitalize shrink-0">
            {style}
          </span>
        )}
      </div>

      {/* Generated images — or in-slot handoff (loader → real image in same geometry) */}
      {generation.status === "completed" && outputUrls.length > 0 && (
        <>
          {handoffImageSlot ? (
            handoffImageSlot
          ) : (
            <div
              className={cn(
                "grid gap-2",
                outputUrls.length === 1
                  ? "grid-cols-1"
                  : isVertical
                    ? "grid-cols-2 sm:grid-cols-4"
                    : "grid-cols-1 sm:grid-cols-2",
                isVertical ? "max-w-2xl" : "max-w-xl"
              )}
            >
              {outputUrls.map((url, i) => (
                <FeedThumbnail
                  key={`${generation.id}-${i}`}
                  generation={generation}
                  url={url}
                  index={i}
                  outputCount={outputUrls.length}
                  cssAspect={cssAspect}
                  aspectRatio={aspectRatio}
                  skipImageEntrance={skipImageEntrance}
                  onZoom={setZoomUrl}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* Error state */}
      {generation.status === "failed" && (
        <div
          className={cn(
            "rounded-lg px-3 py-2 max-w-lg",
            isTosViolationMessage(generation.error_message)
              ? "border border-red-500/45 bg-[#4c2424]/90"
              : "border border-red-500/20 bg-red-500/5"
          )}
        >
          {isTosViolationMessage(generation.error_message) ? (
            <>
              <p className="text-xs font-semibold text-red-200">Terms of Service violation</p>
              <p className="mt-1 text-xs leading-relaxed text-red-100/90">
                {generation.error_message ?? "This prompt is not allowed."}
              </p>
            </>
          ) : (
            <p className="text-xs text-red-400">
              {generation.error_message ?? "Generation failed"}
            </p>
          )}
        </div>
      )}


      {/* Zoom lightbox */}
      <ImageZoomLightbox
        src={zoomUrl ?? ""}
        alt={generation.prompt}
        caption={generation.prompt}
        open={!!zoomUrl}
        onClose={() => setZoomUrl(null)}
      />
    </div>
  )
}

function PendingImageSlot({
  progress,
  aspectRatioCss,
  revealUrl,
  revealAlt = "",
  roundClass = "rounded-none",
  suppressProgressAria = false,
}: {
  progress: number
  aspectRatioCss: string
  revealUrl?: string | null
  revealAlt?: string
  roundClass?: string
  /** When generating multiple images, only the first slot exposes progressbar semantics. */
  suppressProgressAria?: boolean
}) {
  /** Bitmap decoded — show full-opacity blurry image before animating sharp (no empty flash) */
  const [decoded, setDecoded] = useState(false)
  /** Next frame after decode — start blur→sharp (avoids opacity 0→1 gap with loader) */
  const [runSharp, setRunSharp] = useState(false)
  useEffect(() => {
    setDecoded(false)
    setRunSharp(false)
  }, [revealUrl])

  useLayoutEffect(() => {
    if (!decoded) return
    setRunSharp(false)
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => setRunSharp(true))
    })
    return () => cancelAnimationFrame(id)
  }, [decoded])

  const hasRevealImage = Boolean(revealUrl)
  const previewUrl = revealUrl ?? GENERATE_DEFAULT_IMAGE

  const { status, reloadKey, onLoad, onError } = useImageFallback({
    src: previewUrl,
    scope: "generation-feed-pending-slot",
    extra: {
      surface: "generation-feed-pending",
      media_url_host: getUrlHost(previewUrl),
      aspect_ratio: aspectRatioCss,
    },
  })
  const revealFailed = hasRevealImage && status === "error"

  // On a hard reveal failure, drop the loader (as if decoded) so the slot
  // resolves to the branded fallback instead of spinning forever.
  useEffect(() => {
    if (revealFailed) setDecoded(true)
  }, [revealFailed])

  return (
    <div
      className={cn(
        "relative w-full overflow-hidden border border-border/25 bg-background shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]",
        roundClass
      )}
      style={{ aspectRatio: aspectRatioCss }}
    >
      {/* Real pixels: decode → instant full-opacity blurry frame → sharpen (loader overlaps until then) */}
      <div className="absolute inset-0 z-[1] overflow-hidden">
        <motion.div
          className="absolute inset-0"
          initial={false}
          animate={{ opacity: hasRevealImage ? (decoded ? 1 : 0) : 0.62 }}
          transition={{ duration: 0 }}
        >
          <motion.div
            className="absolute inset-0 will-change-[filter]"
            initial={false}
            animate={{
              filter: hasRevealImage && decoded
                ? runSharp
                  ? "blur(0px) brightness(1)"
                  : "blur(36px) brightness(0.92)"
                : "blur(0px) brightness(0.78)",
              scale: hasRevealImage && runSharp ? 1 : 1.02,
            }}
            transition={{
              duration: hasRevealImage && runSharp ? 1.38 : 0,
              ease: [0.09, 0.0, 0.09, 1] as const,
            }}
          >
            <Image
              key={reloadKey}
              src={previewUrl}
              alt={hasRevealImage ? revealAlt : ""}
              fill
              className="object-cover"
              sizes="(max-width: 768px) 90vw, 480px"
              priority={hasRevealImage}
              onLoad={() => {
                onLoad()
                if (hasRevealImage) {
                  setDecoded(true)
                }
              }}
              // Only treat a real result image as a failure. The decorative
              // default placeholder is covered by the loader during
              // generation, so a degraded-optimizer miss on it must not fire
              // the branded fallback or a noisy load-error $exception.
              onError={hasRevealImage ? onError : undefined}
            />
          </motion.div>
        </motion.div>
      </div>

      {/* Waves stay on top until decode; then fade after blurry image is visible (no empty frame) */}
      <motion.div
        className="pointer-events-none absolute inset-0 z-[2]"
        initial={false}
        animate={{ opacity: hasRevealImage && decoded ? 0 : 1 }}
        transition={{
          duration: hasRevealImage && decoded ? 1.12 : 0,
          ease: [0.16, 0.04, 0.16, 1] as const,
          delay: hasRevealImage && decoded ? 0.22 : 0,
        }}
      >
        <GenerationImageLoaderBackdrop progress={progress} />
      </motion.div>

      <motion.div
        className="pointer-events-none absolute inset-0 z-[3] flex items-center justify-center p-3"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{
          opacity: hasRevealImage && decoded ? 0 : 1,
          scale: hasRevealImage && decoded ? 0.94 : 1,
        }}
        transition={{
          opacity: {
            duration: hasRevealImage && decoded ? 0.72 : 0.38,
            ease: [0.2, 0.05, 0.2, 1] as const,
            delay: hasRevealImage && decoded ? 0.16 : 0,
          },
          scale: {
            type: "spring",
            stiffness: 260,
            damping: 26,
            delay: hasRevealImage && decoded ? 0.08 : 0,
          },
        }}
      >
        <SlotProgressRing progress={progress} suppressAria={suppressProgressAria} />
      </motion.div>

      {revealFailed && <ImageFallback className="absolute inset-0 z-[4]" />}
    </div>
  )
}

function HandoffImageSection({
  numImages,
  aspectRatio,
  isPending,
  prompt,
  revealUrls,
  roundClass = "rounded-none",
  backendProgressPercent = 0,
  backendProgressLabel,
}: {
  numImages: number
  aspectRatio: string
  isPending: boolean
  prompt: string
  revealUrls: string[]
  roundClass?: string
  backendProgressPercent?: number
  backendProgressLabel?: string
}) {
  const { progresses, phaseLabel } = useMultiSlotProgress(
    isPending,
    numImages,
    backendProgressPercent
  )
  const cssAspect = aspectRatio.includes(":") ? aspectRatio.replace(":", "/") : "1/1"
  const isVertical = ["9:16", "3:4", "2:3", "4:5"].includes(aspectRatio)

  const statusMaxW = isVertical ? "max-w-2xl" : "max-w-xl"
  const aggregateProgress =
    progresses.length > 0
      ? progresses.reduce((a, b) => a + b, 0) / progresses.length
      : 0
  const aggregateRounded = Math.round(aggregateProgress)
  const statusLabel =
    backendProgressLabel && backendProgressLabel !== "Starting…"
      ? backendProgressLabel
      : phaseLabel

  return (
    <>
      {/* Above the grid so tall aspect ratios don't push status below the fold */}
      <motion.div
        layout
        className={cn(
          "relative flex w-full flex-col gap-3 overflow-hidden rounded-2xl border border-white/[0.09] bg-white/[0.045] px-4 py-3.5 shadow-[0_14px_44px_rgba(0,0,0,0.4)] backdrop-blur-xl",
          statusMaxW
        )}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          duration: 0.42,
          ease: [0.22, 0.1, 0.22, 1] as const,
        }}
        aria-live="polite"
        aria-relevant="additions text"
      >
        {/* Soft brand glow along the top edge */}
        <span
          className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#1d7df2]/70 to-transparent"
          aria-hidden
        />
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 flex-1 items-start gap-2.5">
            <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full border border-white/[0.1] bg-gradient-to-br from-[#1d7df2]/25 to-[#ec4899]/15">
              <Loader2 className="size-3.5 animate-spin text-white/85" aria-hidden />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-white/45">
                Generating
              </p>
              <AnimatePresence mode="wait" initial={false}>
                <motion.p
                  key={statusLabel}
                  initial={{ opacity: 0, x: -8, filter: "blur(4px)" }}
                  animate={{ opacity: 1, x: 0, filter: "blur(0px)" }}
                  exit={{ opacity: 0, x: 6, filter: "blur(3px)" }}
                  transition={{
                    duration: 0.3,
                    ease: [0.22, 0.1, 0.22, 1] as const,
                  }}
                  className="mt-0.5 truncate text-sm font-semibold leading-snug text-white"
                >
                  {statusLabel}
                </motion.p>
              </AnimatePresence>
            </div>
          </div>
          <div
            className="flex shrink-0 items-center self-center rounded-full border border-white/[0.1] bg-white/[0.06] px-2.5 py-1"
            aria-hidden
          >
            <span className="text-xs font-bold tabular-nums text-white">
              {aggregateRounded}
              <span className="text-[10px] font-semibold text-white/55">%</span>
            </span>
          </div>
        </div>
        <div
          className="relative h-1.5 w-full overflow-hidden rounded-full bg-white/[0.07]"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={aggregateRounded}
          aria-valuetext={`About ${aggregateRounded}% complete across images`}
        >
          <motion.div
            className="absolute left-0 top-0 h-full w-full origin-left overflow-hidden rounded-full bg-gradient-to-r from-[#1d7df2] via-[#8b5cf6] to-[#ec4899]"
            initial={false}
            animate={{ scaleX: aggregateProgress / 100 }}
            transition={{
              type: "spring",
              stiffness: 76,
              damping: 18,
            }}
          />
          <motion.div
            className="absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-white/[0.35] to-transparent mix-blend-overlay"
            initial={{ x: "-120%" }}
            animate={{ x: "420%" }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "linear", repeatDelay: 0.4 }}
            aria-hidden
          />
        </div>
      </motion.div>

      <div
        className={cn(
          "grid w-full gap-2",
          numImages === 1
            ? "grid-cols-1"
            : isVertical
              ? "grid-cols-2 sm:grid-cols-4"
              : "grid-cols-1 sm:grid-cols-2",
          isVertical ? "max-w-2xl" : "max-w-xl"
        )}
      >
        {progresses.map((slotProgress, i) => (
          <PendingImageSlot
            key={i}
            progress={slotProgress}
            aspectRatioCss={cssAspect}
            revealUrl={revealUrls[i]}
            revealAlt={prompt}
            roundClass={roundClass}
            suppressProgressAria={numImages > 1 && i > 0}
          />
        ))}
      </div>
    </>
  )
}

function GenerationProgress({
  prompt,
  style,
  numImages,
  aspectRatio,
  isPending,
  hidePromptRow,
  revealUrls = [],
  backendProgressPercent = 0,
  backendProgressLabel,
}: {
  prompt: string
  style: string
  aspectRatio: string
  numImages: number
  isPending: boolean
  hidePromptRow?: boolean
  revealUrls?: string[]
  backendProgressPercent?: number
  backendProgressLabel?: string
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{
        type: "tween",
        duration: 0.45,
        ease: [0.22, 0.1, 0.22, 1] as const,
      }}
      className={cn(
        "relative z-10 flex flex-col gap-3 py-6",
        hidePromptRow && "-mt-3 pb-4 pt-2"
      )}
    >
      {!hidePromptRow && (
        <div className="flex items-start gap-3 max-w-2xl">
          <div className="rounded-lg bg-card/60 border border-border/20 px-3 py-2 max-w-sm">
            <p className="text-sm text-foreground">{prompt}</p>
          </div>
          {style && (
            <span className="mt-1 rounded-full bg-card/40 border border-border/20 px-2 py-0.5 text-[10px] font-medium text-muted-foreground capitalize shrink-0">
              {style}
            </span>
          )}
        </div>
      )}

      <HandoffImageSection
        numImages={numImages}
        aspectRatio={aspectRatio}
        isPending={isPending}
        prompt={prompt}
        revealUrls={revealUrls}
        roundClass="rounded-none"
        backendProgressPercent={backendProgressPercent}
        backendProgressLabel={backendProgressLabel}
      />
    </motion.div>
  )
}

export function GenerationFeed({
  generations,
  pendingGeneration,
  generationProgress,
  isPending,
}: GenerationFeedProps) {
  const backendProgressPercent = generationProgress?.percent ?? 0
  const backendProgressLabel = generationProgress?.label
  // Only show completed/failed generations, oldest-first so newest
  // appears at the bottom (right where the progress card was)
  const feedItems = generations
    .filter((g) => g.status === "completed" || g.status === "failed")
    .slice()
    .reverse()

  /** Keeps loader mounted briefly after isPending so AnimatePresence can exit + images can decode underneath */
  const [pendingSnapshot, setPendingSnapshot] = useState<
    NonNullable<GenerationFeedProps["pendingGeneration"]> | null
  >(null)

  useEffect(() => {
    if (pendingGeneration) setPendingSnapshot(pendingGeneration)
  }, [pendingGeneration])

  const progressConfig = pendingGeneration ?? pendingSnapshot
  const showProgressOverlay = Boolean(progressConfig && (isPending || pendingSnapshot))

  const lastFeed = feedItems[feedItems.length - 1]
  /** In-slot handoff: same row as the new result so loader → image is one surface (no flash) */
  const embedHandoffInLastRow = Boolean(
    showProgressOverlay &&
      pendingSnapshot &&
      !isPending &&
      lastFeed &&
      lastFeed.status === "completed" &&
      lastFeed.prompt === pendingSnapshot.prompt
  )

  const handoffUrlCount = useMemo(() => {
    if (!embedHandoffInLastRow || !lastFeed) return 0
    return getOutputUrls(lastFeed).length
  }, [embedHandoffInLastRow, lastFeed])

  useEffect(() => {
    if (!isPending && pendingSnapshot) {
      const delay = handoffUrlCount > 0 ? 1750 : 560
      const t = window.setTimeout(() => setPendingSnapshot(null), delay)
      return () => clearTimeout(t)
    }
  }, [isPending, pendingSnapshot, handoffUrlCount])

  const orphanProgressOnly = Boolean(showProgressOverlay && progressConfig && !embedHandoffInLastRow)

  /** After embedded handoff unmounts, final grid must not start at opacity 0 (cached images flash) */
  const wasEmbedRef = useRef(false)
  const [skipEntranceForId, setSkipEntranceForId] = useState<string | null>(null)

  useEffect(() => {
    if (embedHandoffInLastRow) wasEmbedRef.current = true
  }, [embedHandoffInLastRow])

  useEffect(() => {
    if (pendingSnapshot === null && wasEmbedRef.current && lastFeed) {
      setSkipEntranceForId(lastFeed.id)
      wasEmbedRef.current = false
      const t = window.setTimeout(() => setSkipEntranceForId(null), 900)
      return () => clearTimeout(t)
    }
  }, [pendingSnapshot, lastFeed])

  if (feedItems.length === 0 && !showProgressOverlay) return null

  return (
    <div className="w-full max-w-2xl mx-auto divide-y divide-border/10">
      {feedItems.map((gen, idx) => {
        const isLast = idx === feedItems.length - 1
        const handoffImageSlot =
          embedHandoffInLastRow && isLast && progressConfig ? (
            <HandoffImageSection
              key="handoff"
              numImages={progressConfig.numImages}
              aspectRatio={progressConfig.aspectRatio}
              isPending={isPending}
              prompt={progressConfig.prompt}
              revealUrls={getOutputUrls(gen)}
              roundClass="rounded-none"
              backendProgressPercent={backendProgressPercent}
              backendProgressLabel={backendProgressLabel}
            />
          ) : undefined

        return (
          <FeedItem
            key={gen.id}
            generation={gen}
            handoffImageSlot={handoffImageSlot}
            skipImageEntrance={skipEntranceForId === gen.id}
          />
        )
      })}

      <AnimatePresence>
        {orphanProgressOnly && progressConfig && (
          <GenerationProgress
            key="pending"
            prompt={progressConfig.prompt}
            style={progressConfig.style}
            aspectRatio={progressConfig.aspectRatio}
            numImages={progressConfig.numImages}
            isPending={isPending}
            hidePromptRow={false}
            revealUrls={[]}
            backendProgressPercent={backendProgressPercent}
            backendProgressLabel={backendProgressLabel}
          />
        )}
      </AnimatePresence>
    </div>
  )
}
